// src/utils/bookingCalculations.ts
// Common booking calculation utilities

/**
 * Calculate total amount for a booking item
 * @param serviceCount - Number of makeup/service units
 * @param pricePerUnit - Price per service unit
 * @returns Total amount (serviceCount × pricePerUnit)
 */
export const calculateBookingItemTotal = (serviceCount: number, pricePerUnit: number): number => {
  const count = serviceCount || 1;
  const price = pricePerUnit || 0;
  return count * price;
};

/**
 * Calculate total amount for all booking items
 * @param bookingItems - Array of booking items
 * @returns Sum of all non-deleted booking items
 */
export const calculateBookingTotal = (bookingItems: any[]): number => {
  if (!bookingItems || !Array.isArray(bookingItems)) return 0;
  
  return bookingItems
    .filter((item: any) => !item.isDeleted)
    .reduce((sum: number, item: any) => {
      const count = item.serviceCount || 1;
      const price = item.amount || 0;
      return sum + calculateBookingItemTotal(count, price);
    }, 0);
};

/**
 * Calculate total service count for a booking
 * @param bookingItems - Array of booking items
 * @returns Sum of serviceCount from all non-deleted items
 */
export const calculateTotalServiceCount = (bookingItems: any[]): number => {
  if (!bookingItems || !Array.isArray(bookingItems)) return 0;
  
  return bookingItems
    .filter((item: any) => !item.isDeleted)
    .reduce((sum: number, item: any) => sum + (item.serviceCount || 1), 0);
};

/**
 * Calculate discount given based on total and booking amount
 * @param totalAmount - Total calculated amount
 * @param bookingAmount - Final booking amount after discount
 * @returns Discount amount
 */
export const calculateDiscountGiven = (totalAmount: number, bookingAmount: number): number => {
  return Math.max(0, totalAmount - bookingAmount);
};

/**
 * Calculate pending amount based on total and paid amount
 * @param bookingAmount - Final booking amount (after discount)
 * @param paidAmount - Amount already paid
 * @returns Pending amount
 */
export const calculatePendingAmount = (bookingAmount: number, paidAmount: number): number => {
  return Math.max(0, bookingAmount - paidAmount);
};

/**
 * Get the latest service price from service price list
 * @param servicePrices - Array of service prices
 * @param serviceId - Service ID to find price for
 * @param effectiveDate - Optional date to find price for (defaults to current date)
 * @returns Latest applicable service price
 */
export const getLatestServicePrice = (
  servicePrices: any[], 
  serviceId: number,
  effectiveDate?: string
): number => {
  if (!servicePrices || !Array.isArray(servicePrices)) return 0;
  
  const prices = servicePrices.filter((p: any) => p.serviceId === serviceId);
  if (prices.length === 0) return 0;
  
  // Sort by effectiveFrom date (most recent first)
  prices.sort((a: any, b: any) => {
    const dateA = new Date(a.effectiveFrom).getTime();
    const dateB = new Date(b.effectiveFrom).getTime();
    return dateB - dateA;
  });
  
  // If specific date provided, find the price effective on that date
  if (effectiveDate) {
    const targetDate = new Date(effectiveDate).getTime();
    const applicablePrice = prices.find((p: any) => {
      const priceDate = new Date(p.effectiveFrom).getTime();
      return priceDate <= targetDate;
    });
    return applicablePrice?.price || prices[0]?.price || 0;
  }
  
  // Return most recent price
  return prices[0].price || 0;
};
